import { useEffect, useMemo, useRef } from 'react';
import { CircleMarker, MapContainer, Popup, TileLayer, Tooltip, useMap } from 'react-leaflet';
import { Maximize2, Minimize2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

import { AppSidebar } from '../../components/AppSidebar/AppSidebar';
import { useFullscreen } from '../../hooks/useFullscreen';
import {
  CUSTOMER_TONE,
  US_CENTER,
  US_ZOOM,
  WAREHOUSE_TONE,
  boundsOf,
  customerPinRadius,
  formatAddressLine,
  locationLabel,
  useMapLocations,
} from '../../features/load-map/api';
import type { MapCustomerLocation } from '../../features/load-map/api';
import './load-map.scss';

const CARTO_BASEMAP_KEY = process.env.REACT_APP_CARTO_BASEMAP_KEY;

/**
 * Frames every pin once the data lands, and again whenever `refitKey` changes —
 * entering full screen roughly doubles the canvas, so the old fit is wrong.
 */
function FitToLoads({
  points,
  refitKey,
}: {
  points: { latitude: number; longitude: number }[];
  refitKey: unknown;
}) {
  const map = useMap();
  const bounds = useMemo(() => boundsOf(points), [points]);

  useEffect(() => {
    if (!bounds) return;

    // Measure before fitting: the container may have just changed size, and
    // fitBounds against a stale size lands off-centre.
    map.invalidateSize(false);
    map.fitBounds(bounds, { padding: [64, 64], maxZoom: 9 });
  }, [bounds, refitKey, map]);

  return null;
}

/** Leaflet measures its container once, so anything that resizes it must say so. */
function ResizeWatcher() {
  const map = useMap();

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => map.invalidateSize(false));
    observer.observe(map.getContainer());

    return () => observer.disconnect();
  }, [map]);

  return null;
}

function CustomerPopup({ location }: { location: MapCustomerLocation }) {
  const occupants = location.occupants;
  return (
    <div className="load-map__popup">
      <div className="load-map__popup-title">{locationLabel(location)}</div>
      <div className="load-map__popup-address">{formatAddressLine(location)}</div>

      <div className="load-map__popup-count">
        {occupants.length} {occupants.length === 1 ? 'location' : 'locations'} here
      </div>

      <ul className="load-map__occupants">
        {occupants.map((o) => (
          <li key={o.addressId}>
            {o.customerName ?? 'Customer'}
            {o.label ? ` · ${o.label}` : ''}
            {o.isPrimary ? ' · primary' : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function LoadMapPage() {
  const { data, isLoading, isError, isFetching } = useMapLocations();
  const shellRef = useRef<HTMLElement>(null);
  const { active: isFullscreen, toggle: toggleFullscreen } = useFullscreen(shellRef);

  // F toggles, Esc leaves — the page has no text inputs to steal the key from,
  // but a stray one would still be typed into rather than swallowed.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'f' && event.key !== 'F') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable) return;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

      event.preventDefault();
      toggleFullscreen();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleFullscreen]);

  const customers = useMemo(() => data?.customers ?? [], [data]);
  const warehouses = useMemo(() => data?.warehouses ?? [], [data]);

  const framePoints = useMemo(
    () =>
      [...customers, ...warehouses].map((p) => ({
        latitude: p.latitude,
        longitude: p.longitude,
      })),
    [customers, warehouses]
  );

  const totals = data?.totals;

  return (
    <div className="layout">
      <AppSidebar />

      <main ref={shellRef} className={`load-map${isFullscreen ? ' load-map--fullscreen' : ''}`}>
        <header className="load-map__header">
          <div>
            <h1 className="load-map__title">Map</h1>
            <div className="muted tiny">
              {totals
                ? `${totals.customerLocations} customer locations · ${totals.warehouses} warehouses`
                : 'Loading…'}
              {isFetching ? ' · Refreshing…' : ''}
            </div>
          </div>

          <div className="load-map__header-actions">
            <ul className="load-map__legend">
              <li className="load-map__legend-item">
                <span className="load-map__legend-dot" style={{ background: CUSTOMER_TONE }} />
                Customer location
              </li>
              <li className="load-map__legend-item">
                <span
                  className="load-map__legend-dot load-map__legend-dot--square"
                  style={{ background: WAREHOUSE_TONE }}
                />
                Warehouse
              </li>
            </ul>

            <button
              type="button"
              className="btn btn--ghost btn--sm load-map__fullscreen"
              onClick={toggleFullscreen}
              aria-pressed={isFullscreen}
              title={isFullscreen ? 'Exit full screen (F or Esc)' : 'Full screen (F)'}
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              {isFullscreen ? 'Exit full screen' : 'Full screen'}
            </button>
          </div>
        </header>

        {totals && totals.unmapped > 0 && (
          <div className="banner banner--warn load-map__notice">
            {totals.unmapped} {totals.unmapped === 1 ? 'location is' : 'locations are'} not shown —
            no city or state to place them by.
          </div>
        )}

        {isError ? (
          <div className="banner banner--warn">Failed to load the map.</div>
        ) : isLoading ? (
          <div className="skeleton load-map__skeleton" />
        ) : (
          <div className="load-map__canvas">
            <MapContainer
              center={US_CENTER}
              zoom={US_ZOOM}
              scrollWheelZoom
              className="load-map__leaflet"
            >
              {/* Positron: grey land, white roads, almost no labels — the point
                  is the pins, not the basemap. */}
              <TileLayer
                url={`https://basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_BASEMAP_KEY}`}
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                maxZoom={19}
              />

              <FitToLoads points={framePoints} refitKey={isFullscreen} />
              <ResizeWatcher />

              {warehouses.map((warehouse) => (
                <CircleMarker
                  key={warehouse.id}
                  center={[warehouse.latitude, warehouse.longitude]}
                  radius={7}
                  pathOptions={{
                    color: '#ffffff',
                    weight: 2,
                    fillColor: WAREHOUSE_TONE,
                    fillOpacity: 1,
                  }}
                >
                  <Tooltip direction="top" offset={[0, -6]}>
                    {warehouse.name ?? 'Warehouse'}
                  </Tooltip>
                  <Popup maxWidth={320}>
                    <div className="load-map__popup">
                      <div className="load-map__popup-title">{warehouse.name ?? 'Warehouse'}</div>
                      <div className="load-map__popup-address">{formatAddressLine(warehouse)}</div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}

              {customers.map((location) => {
                const occupantCount = location.occupants.length;
                const tooltip =
                  occupantCount > 1
                    ? `${locationLabel(location)} — ${occupantCount} locations`
                    : (location.occupants[0]?.customerName ?? locationLabel(location));

                return (
                  <CircleMarker
                    key={location.key}
                    center={[location.latitude, location.longitude]}
                    radius={customerPinRadius(occupantCount)}
                    pathOptions={{
                      color: '#ffffff',
                      weight: 2,
                      fillColor: CUSTOMER_TONE,
                      fillOpacity: 0.85,
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -4]}>
                      {tooltip}
                    </Tooltip>
                    <Popup maxWidth={320}>
                      <CustomerPopup location={location} />
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>
        )}
      </main>
    </div>
  );
}
